import { useQuery } from '@tanstack/react-query';
import { API } from '@/utils/API';

export interface AssignableRole {
  key: string;
  name: string;
}

interface UseAssignableRolesReturn {
  roles: AssignableRole[];
  isLoading: boolean;
  error: string | null;
}

const fetchAssignableRoles = async (
  resourcePath: string
): Promise<AssignableRole[]> => {
  const response = await API.authenticated.fetch(
    `${resourcePath}/assignable_roles`
  );

  if (!response.ok) {
    throw new Error('Failed to fetch assignable roles');
  }

  const data = await response.json();
  return data.roles;
};

export const useAssignableRoles = (
  resourcePath: string,
  { enabled = true }: { enabled?: boolean } = {}
): UseAssignableRolesReturn => {
  const {
    data: roles = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['assignable-roles', resourcePath],
    queryFn: () => fetchAssignableRoles(resourcePath),
    enabled: enabled && !!resourcePath,
    staleTime: 10 * 60 * 1000, // Consider fresh for 10 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });

  return {
    roles,
    isLoading,
    error: error ? (error as Error).message : null,
  };
};
